import React, { useState, useRef } from 'react';
import Pagination from '../../components/Pagination';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { resolveMediaUrl } from '../../config/api';
import useAuthStore from '../../store/authStore';
import ImportProducts from './ImportProducts';

// ─── icons ────────────────────────────────────────────────────────────────────
const IconSearch   = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const IconPlus     = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>;
const IconEdit     = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconTrash    = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>;
const IconArchive  = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="5" rx="1"/><path d="M4 8v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V8"/><path d="M10 12h4"/></svg>;
const IconRestore  = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 12a9 9 0 1 0 9-9 9 9 0 0 0-6.36 2.64L3 8"/><path d="M3 3v5h5"/></svg>;
const IconChevron  = ({ open }) => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}><polyline points="9 18 15 12 9 6"/></svg>;
const IconX        = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>;
const IconEye      = ({ off }) => off
  ? <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
  : <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>;
const IconLock     = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const IconImage    = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>;

const ACCENT = '#2e9f86';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt$ = (v) => v ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';
const costStr = (v, cur) => v ? (cur === 'USD' ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : `ل.ل${Math.round(Number(v)).toLocaleString()}`) : '—';
// Use api.defaults.baseURL so relative paths work on both cloud and local installs
const imgUrl = (url) => resolveMediaUrl(url);
const btn = (bg, color, text = false) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  background: bg, color, border: 'none', borderRadius: 6, cursor: 'pointer',
  padding: text ? '4px 10px' : 6, fontSize: text ? 12 : 14,
  fontWeight: 600, lineHeight: 1, transition: 'opacity 0.15s',
});

const Badge = ({ children, color }) => {
  const map = {
    green:  { bg: '#d1fae5', text: '#065f46' },
    gray:   { bg: '#f3f4f6', text: '#6b7280' },
    purple: { bg: '#ede9fe', text: '#5b21b6' },
    blue:   { bg: '#dbeafe', text: '#1e40af' },
    amber:  { bg: '#fef3c7', text: '#92400e' },
  };
  const c = map[color] || map.gray;
  return (
    <span style={{ background: c.bg, color: c.text, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>{children}</span>
  );
};

const typeColor = { simple: 'green', variable: 'purple', scaled: 'amber' };

const avatarColor = (str) => {
  const palette = ['#2e9f86', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#14b8a6', '#ef4444', '#6366f1'];
  let h = 0; for (let i = 0; i < (str || '').length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return palette[Math.abs(h) % palette.length];
};

const Field = ({ label, children, span3 }) => (
  <div style={{ gridColumn: span3 ? '1/-1' : undefined }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
    {children}
  </div>
);

const inp = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8,
  padding: '7px 11px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s', background: '#fafafa', color: '#111',
};

function normalizeLevels(pl) {
  const out = {};
  if (pl && typeof pl === 'object') {
    for (const [id, val] of Object.entries(pl)) {
      if (val && typeof val === 'object') {
        out[id] = { usd: val.usd ?? '', lbp: val.lbp ?? '' };
      } else if (val !== '' && val != null) {
        out[id] = { usd: val, lbp: '' };
      }
    }
  }
  return out;
}
function cleanLevels(pl) {
  const out = {};
  if (pl && typeof pl === 'object') {
    for (const [id, val] of Object.entries(pl)) {
      const usd = Number(val?.usd) || 0;
      const lbp = Number(val?.lbp) || 0;
      if (usd > 0 || lbp > 0) out[id] = { usd, lbp };
    }
  }
  return out;
}

// ─── Image Upload Component ───────────────────────────────────────────────────
function ImageUpload({ imageUrl, onUploaded, onRemove, disabled }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadError('Only image files allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Max 5MB'); return; }
    setUploadError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(res.data.url);
    } catch (e) {
      setUploadError(e?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const fullUrl = imgUrl(imageUrl);

  return (
    <div>
      {fullUrl ? (
        <div style={{ position: 'relative', width: '100%', height: 160, borderRadius: 10, overflow: 'hidden', border: '1.5px solid #e5e7eb' }}>
          <img src={fullUrl} alt="Product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || uploading}
              style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: ACCENT }}
            >
              {uploading ? 'Uploading…' : '↑ Change'}
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc2626' }}
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => !disabled && inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          style={{
            width: '100%', height: 120, border: `2px dashed ${uploading ? ACCENT : '#d1d5db'}`,
            borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 6, cursor: disabled ? 'default' : 'pointer',
            background: uploading ? '#f0fdf8' : '#fafafa', transition: 'all .15s',
            boxSizing: 'border-box',
          }}
        >
          <IconImage />
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
            {uploading ? 'Uploading…' : 'Click or drag image here'}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af' }}>JPG, PNG, WebP · max 5MB</div>
        </div>
      )}
      {uploadError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{uploadError}</div>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
    </div>
  );
}

// ─── Product Avatar (image or initials) ──────────────────────────────────────
function ProductAvatar({ product }) {
  const url = imgUrl(product.image_url);
  if (url) {
    return (
      <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <img src={url} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <div style={{
      width: 34, height: 34, borderRadius: 9, flexShrink: 0,
      background: avatarColor(product.name), color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 14,
    }}>{(product.name || '?').charAt(0).toUpperCase()}</div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ProductList() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const user      = useAuthStore((s) => s.user);
  const role      = user?.role || 'viewer';
  const canEdit   = role === 'admin' || role === 'manager';
  const canArchive= role === 'admin' || role === 'manager';
  const canDelete = role === 'admin';
  const canSeeCost= role === 'admin';

  const [panelOpen,      setPanelOpen]      = useState(false);
  const [editing,        setEditing]        = useState(null);
  const [savedProductId, setSavedProductId] = useState(null);
  const [expandedId,     setExpandedId]     = useState(null);
  const [search,         setSearch]         = useState('');
  const [error,          setError]          = useState('');
  const [variantError,   setVariantError]   = useState('');
  const [editingVariant, setEditingVariant] = useState(null);
  const [showCost,       setShowCost]       = useState(false);
  const [showArchived,   setShowArchived]   = useState(false);
  const [productTab,     setProductTab]     = useState('all'); // 'all' | 'production' | 'raw'
  const [showImport,     setShowImport]     = useState(false);
  const [showLabels,     setShowLabels]     = useState(false);
  const [page,           setPage]           = useState(1);
  const LIMIT = 100;

  const blankProduct = () => ({
    name: '', name_ar: '', sku: '', barcode: '', type: 'simple',
    category_id: '', cost: '', cost_currency: 'USD', price_currency: 'USD',
    price_lbp: '', price_usd: '', stock: '',
    manage_stock: true, sellable: true, description: '', unit: 'piece',
    price_levels: {}, image_url: '', modifier_group_ids: [],
    min_stock: '', reorder_qty: '',
  });
  const blankVariant = () => ({
    name: '', sku: '', barcode: '', price_usd: '', price_lbp: '',
    cost: '', cost_currency: 'USD', stock: '',
    attributes: [{ name: '', value: '' }],
    price_levels: {}, min_stock: '', reorder_qty: '',
  });

  const [form,        setForm]        = useState(blankProduct);
  const [variantForm, setVariantForm] = useState(blankVariant);

  // ── queries ──────────────────────────────────────────────────────────────
  const { data: productResult, isLoading } = useQuery({
    queryKey: ['products', search, page, showArchived],
    queryFn: () => api.get('/api/products', {
      params: {
        ...(search && { search }),
        ...(!showArchived && { status: 'active' }),
        page,
        limit: LIMIT,
      }
    }).then(r => r.data),
    keepPreviousData: true,
  });
  const products   = productResult?.data  || [];
  const totalProds = productResult?.total || 0;
  const totalPages = productResult?.pages || 1;
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get('/api/categories').then(r => r.data),
  });
  const { data: modifierGroups = [] } = useQuery({
    queryKey: ['modifier-groups'],
    queryFn: () => api.get('/api/modifier-groups').then(r => r.data),
  });
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/settings').then(r => r.data),
  });
  const { data: priceLevels = [] } = useQuery({
    queryKey: ['priceLevels'],
    queryFn: () => api.get('/api/price-levels').then(r => r.data),
  });
  const { data: variants = [], isLoading: variantsLoading } = useQuery({
    queryKey: ['variants', expandedId],
    queryFn: () => api.get(`/api/products/${expandedId}/variants`).then(r => r.data),
    enabled: !!expandedId,
  });
  const panelProductId = savedProductId || (editing?._id);
  const { data: panelVariants = [] } = useQuery({
    queryKey: ['variants', panelProductId],
    queryFn: () => api.get(`/api/products/${panelProductId}/variants`).then(r => r.data),
    enabled: !!panelProductId && form.type === 'variable',
  });

  // ── recipe (production items) ─────────────────────────────────────────────
  const [recipeIngredients, setRecipeIngredients] = useState([]); // [{product_id, qty, unit}]
  const [ingSearch, setIngSearch] = useState('');
  const { data: rawMaterials = [] } = useQuery({
    queryKey: ['raw-materials'],
    queryFn: () => api.get('/api/products', { params: { all: true } }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])).then(list => list.filter(p => p.sellable === false)),
  });
  const { data: existingRecipe } = useQuery({
    queryKey: ['recipe', panelProductId],
    queryFn: () => api.get(`/api/recipes/${panelProductId}`).then(r => r.data),
    enabled: !!panelProductId && form.type === 'production',
    onSuccess: (data) => { if (data?.ingredients) setRecipeIngredients(data.ingredients.map(i => ({ ...i, product_id: String(i.product_id) }))); },
  });
  const saveRecipeMutation = useMutation({
    mutationFn: ({ pid, ingredients }) => api.put(`/api/recipes/${pid}`, { ingredients }),
    onSuccess: () => queryClient.invalidateQueries(['recipe', panelProductId]),
  });

  const rate      = settings?.exchange_rate || 89500;
  const plEnabled          = !!settings?.features?.price_levels;
  const variantsEnabled    = settings?.features?.variants !== false;
  const productionEnabled  = !!settings?.features?.production;
  const labelPrinterEnabled = !!settings?.features?.label_printer;
  const labelConfig = settings?.label_printer_config || {};
  const showLbp   = settings?.currency?.show_lbp ?? true;
  const secCode   = settings?.currency?.secondary_code   || 'LBP';
  const secSymbol = settings?.currency?.secondary_symbol || 'ل.ل';
  const scaleCfg  = settings?.scale || {};
  const scalePrefix = String(scaleCfg.prefix || '20');

  // ── mutations ────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (data) => editing
      ? api.put(`/api/products/${editing._id}`, data)
      : api.post('/api/products', data),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['products']);
      if (!editing && (form.type === 'variable' || form.type === 'production')) setSavedProductId(res.data._id);
      else closePanel();
    },
    onError: (err) => setError(err.response?.data?.error || t('error')),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, status }) => api.put(`/api/products/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries(['products']),
    onError: (err) => alert(err.response?.data?.error || t('error')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/products/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['products']),
    onError: (err) => alert(err.response?.data?.error || t('error')),
  });

  const saveVariantMutation = useMutation({
    mutationFn: (data) => editingVariant
      ? api.put(`/api/variants/${editingVariant._id}`, data)
      : api.post(`/api/products/${panelProductId}/variants`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['variants', panelProductId]);
      queryClient.invalidateQueries(['variants', expandedId]);
      setEditingVariant(null); setVariantForm(blankVariant()); setVariantError('');
    },
    onError: (err) => setVariantError(err.response?.data?.error || t('error')),
  });

  const deleteVariantMutation = useMutation({
    mutationFn: (vid) => api.delete(`/api/variants/${vid}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['variants', panelProductId]);
      queryClient.invalidateQueries(['variants', expandedId]);
    },
  });

  // ── helpers ──────────────────────────────────────────────────────────────
  const openNew = () => {
    setEditing(null); setSavedProductId(null);
    // default the category to the first one so a new product is never uncategorized by accident
    const firstCat = categories[0]?._id || categories[0]?.id || '';
    setForm({ ...blankProduct(), category_id: firstCat ? String(firstCat) : '' });
    setVariantForm(blankVariant());
    setRecipeIngredients([]); setIngSearch('');
    setError(''); setVariantError(''); setPanelOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p); setSavedProductId(null);
    setForm({
      name: p.name, name_ar: p.name_ar || '', sku: p.sku || '',
      barcode: p.barcode || '', type: p.type,
      category_id: p.category_id || '', cost: p.cost || '',
      cost_currency: p.cost_currency || 'USD',
      // priced in LIRA only (price_usd 0) → show the lira amount with LBP selected
      price_currency: (!Number(p.price_usd) && Number(p.price_lbp) && p.type !== 'scaled') ? 'LBP' : 'USD',
      price_lbp: p.price_lbp || '',
      price_usd: (!Number(p.price_usd) && Number(p.price_lbp) && p.type !== 'scaled') ? p.price_lbp : (p.price_usd || p.price_per_unit || ''),
      stock: p.stock || '', manage_stock: p.manage_stock !== false, sellable: p.sellable !== false,
      description: p.description || '', unit: p.unit || 'piece',
      price_levels: normalizeLevels(p.price_levels),
      image_url: p.image_url || '',
      modifier_group_ids: (p.modifier_group_ids || []).map(String),
      min_stock: p.min_stock || '', reorder_qty: p.reorder_qty || '',
    });
    setVariantForm(blankVariant()); setError(''); setVariantError('');
    setRecipeIngredients([]); setIngSearch('');
    setPanelOpen(true);
  };
  const closePanel = () => {
    setPanelOpen(false); setEditing(null);
    setSavedProductId(null); setForm(blankProduct()); setError('');
    setRecipeIngredients([]); setIngSearch('');
  };

  const handleUsd  = (v) => setForm(f => ({ ...f, price_usd: v, price_lbp: v ? (f.price_currency === 'LBP' ? Math.round(Number(v)) : Math.round(Number(v) * rate)) : '' }));
  const handleVUsd = (v) => setVariantForm(f => ({ ...f, price_usd: v, price_lbp: v ? Math.round(Number(v) * rate) : '' }));
  const toggleGroup = (gid) => setForm(f => {
    const cur = f.modifier_group_ids || [];
    return { ...f, modifier_group_ids: cur.includes(gid) ? cur.filter(x => x !== gid) : [...cur, gid] };
  });

  const setLevelUsd = (id, v) => setForm(f => ({
    ...f, price_levels: { ...f.price_levels, [id]: { usd: v, lbp: v ? Math.round(Number(v) * rate) : '' } },
  }));
  const setLevelLbp = (id, v) => setForm(f => ({
    ...f, price_levels: { ...f.price_levels, [id]: { lbp: v, usd: v ? Math.round((Number(v) / rate) * 100) / 100 : '' } },
  }));

  const handleSubmit = (e) => {
    e.preventDefault(); setError('');
    // scale items: barcode must be the 5-digit PLU only
    if (form.type === 'scaled' && form.barcode && !/^\d{1,5}$/.test(form.barcode.trim())) {
      setError(`Scale items need the 5-digit PLU only (e.g. 00123) — not the full barcode from the label. The label "${scalePrefix} 00123 01250 X" → enter 00123.`);
      return;
    }
    saveMutation.mutate({
      ...form,
      barcode: form.type === 'scaled' && form.barcode ? form.barcode.trim().padStart(5, '0') : form.barcode,
      cost: Number(form.cost) || 0,
      // LBP selected → the item is priced in lira (price_usd 0, like the POS does for lira items)
      ...(form.price_currency === 'LBP' && form.type !== 'scaled'
        ? { price_usd: 0, price_lbp: Math.round(Number(form.price_usd)) || 0 }
        : { price_usd: Number(form.price_usd) || 0, price_lbp: Math.round(Number(form.price_usd) * rate) || 0 }),
      ...(form.type === 'scaled' ? { price_per_unit: Number(form.price_usd) || 0 } : {}),
      sellable: form.sellable !== false,
      stock: Number(form.stock) || 0,
      min_stock: Number(form.min_stock) || 0,
      reorder_qty: Number(form.reorder_qty) || 0,
      category_id: form.category_id || null,
      price_levels: cleanLevels(form.price_levels),
      image_url: form.image_url || null,
    });
  };

  const handleVariantSubmit = (e) => {
    e.preventDefault(); setVariantError('');
    saveVariantMutation.mutate({
      ...variantForm,
      price_usd: Number(variantForm.price_usd) || 0,
      price_lbp: Math.round(Number(variantForm.price_usd) * rate) || 0,
      cost: Number(variantForm.cost) || 0,
      stock: Number(variantForm.stock) || 0,
      min_stock: Number(variantForm.min_stock) || 0,
      reorder_qty: Number(variantForm.reorder_qty) || 0,
      attributes: variantForm.attributes.filter(a => a.name && a.value),
      price_levels: cleanLevels(variantForm.price_levels),
    });
  };

  const catName = (id) => categories.find(c => c._id === id)?.name || '—';
  const isVariableMode = form.type === 'variable' && (savedProductId || (editing?.type === 'variable'));
  const updateAttr = (i, field, val) => {
    const attrs = [...variantForm.attributes]; attrs[i][field] = val;
    setVariantForm({ ...variantForm, attributes: attrs });
  };

  const doArchive = (p) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    if (next === 'inactive' && !window.confirm(`Archive "${p.name}"? It will be hidden from lists and POS but kept in all past documents. You can restore it anytime.`)) return;
    archiveMutation.mutate({ id: p._id, status: next });
  };
  const doDelete = (p) => {
    if (!window.confirm(`⚠ PERMANENTLY DELETE "${p.name}"?\n\nThis cannot be undone. Old documents keep the product name on record, but the link is lost forever and you won't be able to restore or report on it.\n\nIf you only want to stop selling it, use Archive instead.\n\nProceed with permanent delete?`)) return;
    deleteMutation.mutate(p._id);
  };

  const visible  = products.filter(p => {
    if (productTab === 'production') return p.type === 'production';
    if (productTab === 'raw') return p.sellable === false;
    return true;
  }); // status filter now server-side
  const colCount = 8 + (showCost && canSeeCost ? 1 : 0);

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', position: 'relative', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>

        {/* Header bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: '1.5px solid #f0f0f0', background: '#fff', flexShrink: 0, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111', margin: 0, flex: 1, minWidth: 140 }}>
            Products
            <span style={{ fontSize: 12, fontWeight: 500, color: '#9ca3af', marginLeft: 8 }}>{totalProds.toLocaleString()} items</span>
          </h2>

          <div style={{ position: 'relative', width: 220 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}><IconSearch /></span>
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search products..." style={{ ...inp, paddingLeft: 34, background: '#f9fafb' }} />
          </div>

          {canSeeCost && (
            <button onClick={() => setShowCost(s => !s)} title="Cost is visible to admins only" style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: showCost ? ACCENT : '#f3f4f6',
              color: showCost ? '#fff' : '#6b7280',
              border: 'none', borderRadius: 8, padding: '8px 12px',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              <IconEye off={!showCost} /> {showCost ? 'Hide Cost' : 'Show Cost'} <IconLock />
            </button>
          )}

          <button onClick={() => setShowArchived(s => !s)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: showArchived ? '#374151' : '#f3f4f6',
            color: showArchived ? '#fff' : '#6b7280',
            border: 'none', borderRadius: 8, padding: '8px 12px',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <IconArchive /> {showArchived ? 'Hide Archived' : 'Archived'}
          </button>

          {labelPrinterEnabled && (
            <button onClick={() => setShowLabels(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              🏷️ Labels
            </button>
          )}

          {canEdit && (
            <button onClick={() => setShowImport(true)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#374151', color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              📥 Import
            </button>
          )}
          {canEdit && (
            <button onClick={openNew} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: ACCENT, color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              <IconPlus /> Add Product
            </button>
          )}
        </div>

        {/* Type tabs */}
        <div style={{ display: 'flex', gap: 4, padding: '8px 16px 0', borderBottom: '1.5px solid #e5e7eb', background: '#fff' }}>
          {[
            { key: 'all', label: 'All Products' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setProductTab(tab.key)} style={{
              padding: '7px 16px', border: 'none', borderRadius: '8px 8px 0 0', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: productTab === tab.key ? '#fff' : 'transparent',
              color: productTab === tab.key ? ACCENT : '#6b7280',
              borderBottom: productTab === tab.key ? `2.5px solid ${ACCENT}` : '2.5px solid transparent',
            }}>{tab.label}</button>
          ))}
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflow: 'auto', background: '#f8f9fa' }}>
          {isLoading ? (
            <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          ) : visible.length === 0 ? (
            products.length === 0 ? (
              /* no products at all — onboarding empty state */
              <div style={{ padding: '60px 24px', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#374151', marginBottom: 6 }}>
                  ما في منتجات بعد / No products yet
                </div>
                <div style={{ color: '#9ca3af', fontSize: 13, marginBottom: 20 }}>
                  أضف أول منتج للبدء بالبيع · Add your first product to start selling
                </div>
                <button
                  onClick={openNew}
                  style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                >
                  + أضف منتج / Add Product
                </button>
              </div>
            ) : (
              /* filter returned nothing */
              <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>No products match your search.</div>
            )
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fff', borderBottom: '1.5px solid #f0f0f0' }}>
                  {['', 'Product', 'Type', 'Category', 'Price (USD)',
                    ...(showCost && canSeeCost ? ['Cost'] : []),
                    'Stock', 'Status', ''].map((h, i) => (
                    <th key={i} style={{
                      textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700,
                      color: h === 'Cost' ? ACCENT : '#9ca3af', textTransform: 'uppercase',
                      letterSpacing: 0.5, whiteSpace: 'nowrap', position: 'sticky', top: 0,
                      background: '#fff', zIndex: 1,
                    }}>{h}{h === 'Cost' && <span style={{ marginLeft: 4 }}>🔒</span>}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((p, idx) => {
                  const archived = p.status !== 'active';
                  return (
                  <React.Fragment key={p._id}>
                    <tr
                      style={{
                        background: archived ? '#fafafa' : (idx % 2 === 0 ? '#fff' : '#fafafa'),
                        borderBottom: '1px solid #f3f4f6', opacity: archived ? 0.6 : 1,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                      onMouseLeave={e => e.currentTarget.style.background = archived ? '#fafafa' : (idx % 2 === 0 ? '#fff' : '#fafafa')}
                    >
                      <td style={{ padding: '10px 8px 10px 16px', width: 28 }}>
                        {p.type === 'variable' && variantsEnabled && (
                          <button onClick={() => setExpandedId(expandedId === p._id ? null : p._id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 2, lineHeight: 1 }}>
                            <IconChevron open={expandedId === p._id} />
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <ProductAvatar product={p} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                              {p.sku || 'no-sku'}{p.name_ar ? ' · ' : ''}<span style={{ fontFamily: 'inherit', direction: 'rtl' }}>{p.name_ar}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 14px' }}><Badge color={p.sellable === false ? 'amber' : p.type === 'production' ? 'blue' : (typeColor[p.type] || 'gray')}>{p.sellable === false ? '🥕 raw' : p.type === 'production' ? '🏭 production' : (p.type === 'scaled' ? '⚖️ scaled' : p.type)}</Badge></td>
                      <td style={{ padding: '10px 14px', color: '#6b7280' }}>{catName(p.category_id)}</td>
                      <td style={{ padding: '10px 14px', fontWeight: 700, color: '#111' }}>{p.type === 'variable' ? <span style={{ color: '#9ca3af' }}>—</span> : (!Number(p.price_usd) && Number(p.price_lbp) ? `${Math.round(p.price_lbp).toLocaleString('en-US')} ${secCode}` : fmt$(p.price_usd))}</td>
                      {showCost && canSeeCost && (
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: ACCENT, background: '#f0fdf8' }}>
                          {p.type === 'variable' ? '—' : costStr(p.cost, p.cost_currency)}
                        </td>
                      )}
                      <td style={{ padding: '10px 14px' }}>
                        {p.type === 'variable' ? <span style={{ color: '#9ca3af' }}>—</span>
                          : p.manage_stock === false ? <Badge color="gray">service</Badge>
                          : <span style={{ fontWeight: 600, color: (p.stock || 0) < 5 ? '#dc2626' : '#111' }}>{p.stock || 0}</span>}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <Badge color={archived ? 'gray' : 'green'}>{archived ? 'Archived' : 'Active'}</Badge>
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {canEdit && <button onClick={() => openEdit(p)} title="Edit" style={btn('#eff6ff', '#2563eb')}><IconEdit /></button>}
                          {canArchive && (
                            <button onClick={() => doArchive(p)} title={archived ? 'Restore' : 'Archive'} style={btn(archived ? '#ecfdf5' : '#fffbeb', archived ? '#059669' : '#d97706')}>
                              {archived ? <IconRestore /> : <IconArchive />}
                            </button>
                          )}
                          {canDelete && <button onClick={() => doDelete(p)} title="Permanent delete" style={btn('#fef2f2', '#dc2626')}><IconTrash /></button>}
                        </div>
                      </td>
                    </tr>

                    {expandedId === p._id && (
                      <tr style={{ background: '#f0fdf8' }}>
                        <td colSpan={colCount} style={{ padding: '12px 20px 16px 56px' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Variants — {p.name}
                          </div>
                          {variantsLoading ? <div style={{ color: '#9ca3af', fontSize: 12 }}>Loading…</div>
                            : variants.length === 0 ? <div style={{ color: '#9ca3af', fontSize: 12 }}>No variants yet.</div>
                            : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    {['Variant', 'Attributes', 'SKU', 'Price USD',
                                      ...(showCost && canSeeCost ? ['Cost'] : []), 'Stock'].map(h => (
                                      <th key={h} style={{ textAlign: 'left', padding: '4px 10px', color: h === 'Cost' ? ACCENT : '#6b7280', fontWeight: 600, fontSize: 11 }}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {variants.map(v => (
                                    <tr key={v._id} style={{ borderTop: '1px solid #d1fae5' }}>
                                      <td style={{ padding: '6px 10px', fontWeight: 600, color: '#111' }}>{v.name}</td>
                                      <td style={{ padding: '6px 10px' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                          {v.attributes?.map((a, i) => (
                                            <span key={i} style={{ background: '#d1fae5', color: '#065f46', fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>{a.name}: {a.value}</span>
                                          ))}
                                        </div>
                                      </td>
                                      <td style={{ padding: '6px 10px', color: '#6b7280', fontFamily: 'monospace' }}>{v.sku || '—'}</td>
                                      <td style={{ padding: '6px 10px', fontWeight: 700 }}>{fmt$(v.price_usd)}</td>
                                      {showCost && canSeeCost && <td style={{ padding: '6px 10px', fontWeight: 600, color: ACCENT }}>{costStr(v.cost, v.cost_currency)}</td>}
                                      <td style={{ padding: '6px 10px', fontWeight: 600 }}>{v.stock || 0}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )})}
              </tbody>
            </table>
          )}
          <div style={{ padding: '0 8px' }}>
            <Pagination page={page} pages={totalPages} total={totalProds} limit={LIMIT} onChange={setPage} />
          </div>
        </div>
      </div>

      {/* ── Slide-in panel ── */}
      <>
        {panelOpen && <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40, backdropFilter: 'blur(2px)' }} />}

        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: panelOpen ? 520 : 0, overflow: 'hidden', background: '#fff',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 50,
          transition: 'width 0.3s cubic-bezier(.4,0,.2,1)', display: 'flex', flexDirection: 'column',
        }}>
          {panelOpen && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1.5px solid #f0f0f0', flexShrink: 0 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>{editing ? 'Edit Product' : 'New Product'}</div>
                  {showLbp && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>1 USD = {rate.toLocaleString()} {secCode}</div>}
                </div>
                <button onClick={closePanel} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}><IconX /></button>
              </div>

              <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>

                    {/* Image upload */}
                    <Field label="Product Image" span3>
                      <ImageUpload
                        imageUrl={form.image_url}
                        onUploaded={(url) => setForm(f => ({ ...f, image_url: url }))}
                        onRemove={() => setForm(f => ({ ...f, image_url: '' }))}
                        disabled={saveMutation.isPending}
                      />
                    </Field>

                    <Field label="Name (EN) *" span3>
                      <input type="text" value={form.name} required onChange={e => setForm({ ...form, name: e.target.value })} style={inp} />
                    </Field>
                    <Field label="Name (AR)">
                      <input type="text" value={form.name_ar} dir="rtl" onChange={e => setForm({ ...form, name_ar: e.target.value })} style={inp} />
                    </Field>
                    <Field label="Type *">
                      <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} disabled={!!savedProductId} style={{ ...inp, background: savedProductId ? '#f3f4f6' : '#fafafa' }}>
                        <option value="simple">Simple</option>
                        <option value="scaled">Scaled (sold by weight)</option>
                      </select>
                    </Field>
                    <Field label="SKU">
                      <input type="text" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} style={inp} />
                    </Field>
                    <Field label={form.type === 'scaled' ? 'PLU Code (5 digits)' : 'Barcode'}>
                      <input
                        type="text"
                        value={form.barcode}
                        onChange={e => setForm({ ...form, barcode: form.type === 'scaled' ? e.target.value.replace(/\D/g, '').slice(0, 5) : e.target.value })}
                        placeholder={form.type === 'scaled' ? 'e.g. 00123' : ''}
                        maxLength={form.type === 'scaled' ? 5 : undefined}
                        style={form.type === 'scaled' ? { ...inp, borderColor: '#f59e0b', background: '#fffbeb', fontFamily: 'monospace', letterSpacing: 1 } : inp}
                      />
                    </Field>
                    {form.type === 'scaled' && (
                      <div style={{ gridColumn: '1/-1', fontSize: 11.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 12px', lineHeight: 1.6 }}>
                        ⚖️ <b>Scale item:</b> enter only the 5-digit PLU — the product number programmed in the scale — NOT the full barcode from the label.
                        <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                          Label: <span style={{ color: '#9ca3af' }}>{scalePrefix}</span>
                          {' '}<b style={{ color: '#b45309' }}>{(form.barcode || '· · · · ·').padStart(5, '0')}</b>
                          {' '}<span style={{ color: '#9ca3af' }}>WWWWW C</span>
                          {'  '}← enter only the <b style={{ color: '#b45309' }}>middle part</b>
                        </div>
                      </div>
                    )}
                    <Field label="Category" span3>
                      <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} style={inp}>
                        <option value="">— None —</option>
                        {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                      </select>
                    </Field>
                    {form.type !== 'production' && (
                    <Field label="Cost" span3>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="number" value={form.cost} min="0" step="0.01" onChange={e => setForm({ ...form, cost: e.target.value })} style={{ ...inp, flex: 1 }} />
                        <select value={form.cost_currency} onChange={e => setForm({ ...form, cost_currency: e.target.value })} style={{ ...inp, width: 80, flex: 'none' }}>
                          <option value="USD">USD</option>
                          {showLbp && <option value={secCode}>{secCode}</option>}
                        </select>
                      </div>
                    </Field>
                    )}
                    {!(form.type === 'simple' && form.sellable === false) && form.type !== 'production' && (
                    <Field label={form.type === 'scaled' ? `Price per ${form.unit || 'kg'} (USD)` : form.type === 'variable' ? `Default Price ${form.price_currency === 'LBP' ? secCode : 'USD'} (inherited by variants with no price)` : `Price ${form.price_currency === 'LBP' ? secCode : 'USD'}`} span3>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input type="number" value={form.price_usd} min="0" step={form.price_currency === 'LBP' ? '1000' : '0.01'} onChange={e => handleUsd(e.target.value)} style={{ ...inp, flex: 1, borderColor: ACCENT, background: '#f0fdf8' }} placeholder="0" />
                        {showLbp && form.type !== 'scaled' && (
                          <select value={form.price_currency || 'USD'} onChange={e => setForm(f => ({ ...f, price_currency: e.target.value, price_lbp: f.price_usd ? (e.target.value === 'LBP' ? Math.round(Number(f.price_usd)) : Math.round(Number(f.price_usd) * rate)) : '' }))} style={{ ...inp, width: 80, flex: 'none' }}>
                            <option value="USD">USD</option>
                            <option value="LBP">{secCode}</option>
                          </select>
                        )}
                      </div>
                      {form.price_currency === 'LBP' && Number(form.price_usd) > 0 && rate > 0 && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>≈ ${(Number(form.price_usd) / rate).toFixed(2)} · {secCode} price stays fixed when the rate changes</div>
                      )}
                    </Field>
                    )}
                    {form.type === 'production' && (
                    <Field label={`Selling Price (${form.price_currency === 'LBP' ? secCode : 'USD'})`} span3>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input type="number" value={form.price_usd} min="0" step={form.price_currency === 'LBP' ? '1000' : '0.01'} onChange={e => handleUsd(e.target.value)} style={{ ...inp, flex: 1, borderColor: ACCENT, background: '#f0fdf8' }} placeholder="0" />
                        {showLbp && (
                          <select value={form.price_currency || 'USD'} onChange={e => setForm(f => ({ ...f, price_currency: e.target.value, price_lbp: f.price_usd ? (e.target.value === 'LBP' ? Math.round(Number(f.price_usd)) : Math.round(Number(f.price_usd) * rate)) : '' }))} style={{ ...inp, width: 80, flex: 'none' }}>
                            <option value="USD">USD</option>
                            <option value="LBP">{secCode}</option>
                          </select>
                        )}
                      </div>
                    </Field>
                    )}
                    {form.type === 'simple' && (
                      <Field label="Track Stock">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151', padding: '7px 0' }}>
                          <input
                            type="checkbox"
                            checked={form.manage_stock}
                            onChange={e => setForm({ ...form, manage_stock: e.target.checked, stock: e.target.checked ? form.stock : '' })}
                          />
                          <span>{form.manage_stock ? 'Physical product' : '🛠 Service — no stock'}</span>
                        </label>
                      </Field>
                    )}
                    {form.type === 'simple' && form.manage_stock && (
                      <Field label="Stock Qty">
                        <input type="number" value={form.stock} min="0" onChange={e => setForm({ ...form, stock: e.target.value })} style={inp} />
                      </Field>
                    )}
                    {form.manage_stock && form.type !== 'production' && (
                      <Field label="Min stock (reorder level)">
                        <input type="number" value={form.min_stock} min="0" placeholder="shop default" title="At or under this the item shows as LOW and in Stock → Reorder"
                          onChange={e => setForm({ ...form, min_stock: e.target.value })} style={inp} />
                      </Field>
                    )}
                    {form.manage_stock && form.type !== 'production' && (
                      <Field label="Reorder qty">
                        <input type="number" value={form.reorder_qty} min="0" placeholder="auto" title="How many to buy when it runs low"
                          onChange={e => setForm({ ...form, reorder_qty: e.target.value })} style={inp} />
                      </Field>
                    )}
                    {form.type === 'simple' && !form.manage_stock && (
                      <div style={{ gridColumn: '1/-1', fontSize: 11.5, color: '#1e40af', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: '8px 12px' }}>
                        🛠 Service item: repairs, labor, fees… Sells normally in POS and counts in revenue, but never affects stock.
                      </div>
                    )}
                    <Field label="Unit">
                      <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} style={inp}>
                        {['piece', 'kg', 'g', 'L', 'ml', 'lb', 'm', 'cm', 'box'].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </Field>
                    <Field label="Description" span3>
                      <textarea value={form.description} rows={2} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inp, resize: 'none' }} />
                    </Field>

                    {/* Recipe / Ingredients (production items) */}
                    {form.type === 'production' && panelProductId && (
                      <div style={{ gridColumn: '1/-1', marginTop: 4, borderTop: '1.5px dashed #e5e7eb', paddingTop: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          🏭 Recipe — Ingredients
                        </div>
                        {/* ingredient rows */}
                        {recipeIngredients.map((ing, idx) => {
                          const mat = rawMaterials.find(m => String(m._id) === String(ing.product_id));
                          return (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 7, padding: '6px 10px' }}>
                                🥕 {mat?.name || ing.product_id}
                              </div>
                              <input type="number" min="0.001" step="any" value={ing.qty}
                                onChange={e => setRecipeIngredients(list => list.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))}
                                style={{ ...inp, width: 80 }} placeholder="qty" />
                              <select value={ing.unit}
                                onChange={e => setRecipeIngredients(list => list.map((x, i) => i === idx ? { ...x, unit: e.target.value } : x))}
                                style={{ ...inp, width: 72 }}>
                                {['kg', 'g', 'L', 'ml', 'piece', 'box'].map(u => <option key={u}>{u}</option>)}
                              </select>
                              <button type="button" onClick={() => setRecipeIngredients(list => list.filter((_, i) => i !== idx))}
                                style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '6px 9px', cursor: 'pointer', color: '#b91c1c', fontSize: 14, fontWeight: 700 }}>✕</button>
                            </div>
                          );
                        })}
                        {/* add ingredient row */}
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <input value={ingSearch} onChange={e => setIngSearch(e.target.value)}
                            placeholder="Search raw material…"
                            style={{ ...inp, flex: 1 }} />
                        </div>
                        {ingSearch.trim() && (
                          <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 8, marginTop: 4, maxHeight: 160, overflowY: 'auto', background: '#fff' }}>
                            {rawMaterials.filter(m => m.name?.toLowerCase().includes(ingSearch.toLowerCase()) && !recipeIngredients.some(i => String(i.product_id) === String(m._id))).map(m => (
                              <div key={m._id} onClick={() => { setRecipeIngredients(list => [...list, { product_id: String(m._id), qty: 1, unit: 'piece' }]); setIngSearch(''); }}
                                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: '#374151', borderBottom: '1px solid #f3f4f6' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f0fdf8'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                🥕 {m.name}
                              </div>
                            ))}
                            {rawMaterials.filter(m => m.name?.toLowerCase().includes(ingSearch.toLowerCase()) && !recipeIngredients.some(i => String(i.product_id) === String(m._id))).length === 0 && (
                              <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No raw materials found</div>
                            )}
                          </div>
                        )}
                        {/* save recipe button */}
                        <button type="button"
                          onClick={() => saveRecipeMutation.mutate({ pid: panelProductId, ingredients: recipeIngredients })}
                          disabled={saveRecipeMutation.isPending}
                          style={{ marginTop: 10, padding: '7px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                          {saveRecipeMutation.isPending ? 'Saving…' : '💾 Save Recipe'}
                        </button>
                        {saveRecipeMutation.isSuccess && <span style={{ marginLeft: 10, fontSize: 12, color: '#059669' }}>✓ Saved — Cost auto-set to ${saveRecipeMutation.data?.data?.total_cost_usd?.toFixed(2) ?? '—'}</span>}
                      </div>
                    )}
                    {form.type === 'production' && !panelProductId && (
                      <div style={{ gridColumn: '1/-1', fontSize: 12, color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
                        💡 Save the product first, then the recipe editor will appear.
                      </div>
                    )}

                    {/* Add-on groups */}
                    {modifierGroups.length > 0 && form.type !== 'variable' && (
                      <div style={{ gridColumn: '1/-1', marginTop: 4, borderTop: '1.5px dashed #e5e7eb', paddingTop: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Add-ons
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {modifierGroups.map(g => {
                            const on = (form.modifier_group_ids || []).includes(String(g._id));
                            return (
                              <button type="button" key={g._id} onClick={() => toggleGroup(String(g._id))}
                                style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                  border: `1.5px solid ${on ? ACCENT : '#e5e7eb'}`, background: on ? '#f0fdf8' : '#fafafa', color: on ? '#065f46' : '#374151' }}>
                                {on ? '✓ ' : ''}{g.name}
                              </button>
                            );
                          })}
                        </div>
                        <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 6 }}>
                          Tap to attach option groups (managed in the Add-ons tab). They pop up when ringing up this item.
                        </div>
                      </div>
                    )}

                    {/* Price Levels */}
                    {plEnabled && form.type !== 'variable' && priceLevels.length > 0 && (
                      <div style={{ gridColumn: '1/-1', marginTop: 4, borderTop: '1.5px dashed #e5e7eb', paddingTop: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Price Levels
                        </div>
                        {priceLevels.map(lvl => {
                          const cur = form.price_levels?.[lvl._id] || {};
                          return (
                            <div key={lvl._id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                              <div style={{ width: 110, flexShrink: 0, fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {lvl.name_en}
                                {lvl.name_ar ? <span style={{ color: '#9ca3af', fontWeight: 400 }}> · {lvl.name_ar}</span> : null}
                              </div>
                              <div style={{ flex: 1, position: 'relative' }}>
                                <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: ACCENT, fontSize: 12, fontWeight: 700 }}>$</span>
                                <input type="number" min="0" step="any" value={cur.usd ?? ''} onChange={e => setLevelUsd(lvl._id, e.target.value)} placeholder="USD"
                                  style={{ ...inp, paddingLeft: 22, borderColor: ACCENT, background: '#f0fdf8' }} />
                              </div>
                              {showLbp && (
                                <div style={{ flex: 1, position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 11, fontWeight: 600 }}>{secSymbol}</span>
                                  <input type="number" min="0" step="any" value={cur.lbp ?? ''} onChange={e => setLevelLbp(lvl._id, e.target.value)} placeholder="LBP"
                                    style={{ ...inp, paddingLeft: 30 }} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 2 }}>
                          {showLbp ? `USD is primary; ${secCode} auto-fills at the current rate.` : 'Enter USD price per level.'} Leave blank to skip a level.
                        </div>
                      </div>
                    )}
                  </div>

                  {error && <div style={{ margin: '12px 0', padding: '8px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: 8, fontSize: 13 }}>{error}</div>}

                  {!savedProductId && (
                    <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                      <button type="submit" disabled={saveMutation.isPending} style={{ flex: 1, background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: saveMutation.isPending ? 0.6 : 1 }}>
                        {saveMutation.isPending ? 'Saving…' : form.type === 'variable' && !editing ? 'Save & Add Variants →' : 'Save Product'}
                      </button>
                      <button type="button" onClick={closePanel} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  )}
                </form>

                {/* Variants section */}
                {isVariableMode && (
                  <div style={{ marginTop: 24, borderTop: '1.5px solid #f0f0f0', paddingTop: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Variants</div>
                      {(editing || savedProductId) && (
                        <button onClick={closePanel} style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✓ Done</button>
                      )}
                    </div>

                    <form onSubmit={handleVariantSubmit} style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, border: '1.5px solid #e5e7eb', marginBottom: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{editingVariant ? 'Edit Variant' : 'Add Variant'}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <Field label="Name *" span3>
                          <input type="text" value={variantForm.name} required onChange={e => setVariantForm({ ...variantForm, name: e.target.value })} placeholder="e.g. Large Red" style={inp} />
                        </Field>
                        <Field label="Price USD">
                          <input type="number" value={variantForm.price_usd} min="0" step="0.01" onChange={e => handleVUsd(e.target.value)} style={{ ...inp, borderColor: ACCENT, background: '#f0fdf8' }} placeholder="0" />
                        </Field>
                        {canSeeCost && (
                          <Field label="Cost 🔒">
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input type="number" value={variantForm.cost} min="0" step="0.01" onChange={e => setVariantForm({ ...variantForm, cost: e.target.value })} style={{ ...inp, flex: 1 }} placeholder="0" />
                              <select value={variantForm.cost_currency} onChange={e => setVariantForm({ ...variantForm, cost_currency: e.target.value })} style={{ ...inp, width: 72, flex: 'none' }}>
                                <option value="USD">USD</option>
                                {showLbp && <option value={secCode}>{secCode}</option>}
                              </select>
                            </div>
                          </Field>
                        )}
                        <Field label="SKU">
                          <input type="text" value={variantForm.sku} onChange={e => setVariantForm({ ...variantForm, sku: e.target.value })} style={inp} />
                        </Field>
                        <Field label="Barcode">
                          <input type="text" value={variantForm.barcode} onChange={e => setVariantForm({ ...variantForm, barcode: e.target.value })} style={inp} />
                        </Field>
                        <Field label="Stock">
                          <input type="number" value={variantForm.stock} min="0" onChange={e => setVariantForm({ ...variantForm, stock: e.target.value })} style={inp} />
                        </Field>
                        <Field label="Min stock">
                          <input type="number" value={variantForm.min_stock} min="0" placeholder="item's" onChange={e => setVariantForm({ ...variantForm, min_stock: e.target.value })} style={inp} />
                        </Field>
                        <Field label="Reorder qty">
                          <input type="number" value={variantForm.reorder_qty} min="0" placeholder="auto" onChange={e => setVariantForm({ ...variantForm, reorder_qty: e.target.value })} style={inp} />
                        </Field>
                        {/* Price Levels per variant */}
                        {plEnabled && priceLevels.length > 0 && (
                          <div style={{ gridColumn: '1/-1', borderTop: '1.5px dashed #e5e7eb', paddingTop: 10, marginTop: 2 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: ACCENT, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Price Levels</div>
                            {priceLevels.map(lvl => {
                              const cur = variantForm.price_levels?.[lvl._id] || {};
                              return (
                                <div key={lvl._id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <div style={{ width: 90, flexShrink: 0, fontSize: 11, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {lvl.name_en}
                                  </div>
                                  <div style={{ flex: 1, position: 'relative' }}>
                                    <span style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: ACCENT, fontSize: 11, fontWeight: 700 }}>$</span>
                                    <input type="number" min="0" step="any" value={cur.usd ?? ''} placeholder="USD"
                                      onChange={e => {
                                        const v = e.target.value;
                                        setVariantForm(f => ({ ...f, price_levels: { ...f.price_levels, [lvl._id]: { usd: v, lbp: v ? Math.round(Number(v) * rate) : '' } } }));
                                      }}
                                      style={{ ...inp, paddingLeft: 20, borderColor: ACCENT, background: '#f0fdf8', fontSize: 12 }} />
                                  </div>
                                  {showLbp && (
                                    <div style={{ flex: 1, position: 'relative' }}>
                                      <span style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 10, fontWeight: 600 }}>{secSymbol}</span>
                                      <input type="number" min="0" step="any" value={cur.lbp ?? ''} placeholder="LBP"
                                        onChange={e => {
                                          const v = e.target.value;
                                          setVariantForm(f => ({ ...f, price_levels: { ...f.price_levels, [lvl._id]: { lbp: v, usd: v ? Math.round((Number(v) / rate) * 100) / 100 : '' } } }));
                                        }}
                                        style={{ ...inp, paddingLeft: 26, fontSize: 12 }} />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <Field label="Attributes" span3>
                          {variantForm.attributes.map((attr, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                              <input type="text" placeholder="Name (Size)" value={attr.name} onChange={e => updateAttr(i, 'name', e.target.value)} style={{ ...inp, flex: 1 }} />
                              <input type="text" placeholder="Value (L)" value={attr.value} onChange={e => updateAttr(i, 'value', e.target.value)} style={{ ...inp, flex: 1 }} />
                              <button type="button" onClick={() => setVariantForm({ ...variantForm, attributes: variantForm.attributes.filter((_, idx) => idx !== i) })} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontWeight: 700, fontSize: 16, padding: '0 4px' }}>✕</button>
                            </div>
                          ))}
                          <button type="button" onClick={() => setVariantForm({ ...variantForm, attributes: [...variantForm.attributes, { name: '', value: '' }] })} style={{ background: 'none', border: 'none', color: ACCENT, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>+ Add Attribute</button>
                        </Field>
                      </div>
                      {variantError && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{variantError}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button type="submit" disabled={saveVariantMutation.isPending} style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saveVariantMutation.isPending ? 0.6 : 1 }}>
                          {saveVariantMutation.isPending ? 'Saving…' : editingVariant ? 'Update' : 'Add Variant'}
                        </button>
                        {editingVariant && (
                          <button type="button" onClick={() => { setEditingVariant(null); setVariantForm(blankVariant()); }} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        )}
                      </div>
                    </form>

                    {panelVariants.map(v => (
                      <div key={v._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 8, marginBottom: 8 }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#111' }}>{v.name}</div>
                          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>{fmt$(v.price_usd)}</span>
                            {canSeeCost && v.cost ? <span style={{ fontSize: 11, color: '#9ca3af' }}>Cost: {costStr(v.cost, v.cost_currency)}</span> : null}
                            <span style={{ fontSize: 11, color: '#6b7280' }}>· Stock: {v.stock || 0}</span>
                          </div>
                          {v.attributes?.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {v.attributes.map((a, i) => (
                                <span key={i} style={{ background: '#d1fae5', color: '#065f46', fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 600 }}>{a.name}: {a.value}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => {
                            setEditingVariant(v);
                            setVariantForm({
                              name: v.name, sku: v.sku || '', barcode: v.barcode || '',
                              price_usd: v.price_usd || '', price_lbp: v.price_lbp || '',
                              cost: v.cost || '', cost_currency: v.cost_currency || 'USD',
                              stock: v.stock || '', min_stock: v.min_stock || '', reorder_qty: v.reorder_qty || '',
                              attributes: v.attributes?.length ? v.attributes : [{ name: '', value: '' }],
                              price_levels: normalizeLevels(v.price_levels),
                            });
                          }} style={btn('#eff6ff', '#2563eb', true)}>Edit</button>
                          <button onClick={() => { if (window.confirm('Delete?')) deleteVariantMutation.mutate(v._id); }} style={btn('#fef2f2', '#dc2626', true)}>Del</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </>

      {/* Excel import wizard */}
      {showImport && (
        <ImportProducts
          onClose={() => setShowImport(false)}
          products={products}
          categories={categories}
          rate={rate}
        />
      )}

      {/* Barcode label printer */}
      {showLabels && (
        <PrintLabelsModal
          products={products.filter(p => p.status !== 'archived')}
          rate={rate}
          labelConfig={labelConfig}
          shopName={settings?.company?.name || ''}
          onClose={() => setShowLabels(false)}
        />
      )}
    </div>
  );
}

// ── PrintLabelsModal ──────────────────────────────────────────────────────────
const LABEL_DESIGNS = [
  { id: 'standard', name: 'Standard',    desc: 'Name · Barcode · Price' },
  { id: 'price',    name: 'Price Focus', desc: 'Big price + barcode' },
  { id: 'minimal',  name: 'Minimal',     desc: 'Barcode + price only' },
];
const BARCODE_FORMATS = [
  { value: 'CODE128', label: 'CODE128',  hint: 'Any text or number (recommended)' },
  { value: 'EAN13',   label: 'EAN-13',   hint: '13-digit retail barcodes' },
  { value: 'EAN8',    label: 'EAN-8',    hint: '8-digit compact barcodes' },
  { value: 'UPCA',    label: 'UPC-A',    hint: '12-digit US product codes' },
  { value: 'CODE39',  label: 'CODE39',   hint: 'Uppercase letters + digits' },
  { value: 'ITF14',   label: 'ITF-14',   hint: '14-digit shipping/carton' },
];

function PrintLabelsModal({ products, rate, labelConfig = {}, shopName = '', onClose }) {
  const [search,     setSearch]     = useState('');
  const [copies,     setCopies]     = useState({});
  const [showLBP,    setShowLBP]    = useState(false);
  const [showShop,   setShowShop]   = useState(!!shopName);
  const [mode,       setMode]       = useState(labelConfig.mode      || 'roll');
  const [widthMm,    setWidthMm]    = useState(labelConfig.width_mm  || 50);
  const [heightMm,   setHeightMm]   = useState(labelConfig.height_mm || 30);
  const [columns,    setColumns]    = useState(labelConfig.columns   || 1);
  const [bcFormat,   setBcFormat]   = useState('CODE128');
  const [design,     setDesign]     = useState('standard');
  const [marginMm,   setMarginMm]   = useState(1.5); // inner padding inside each label
  const [gapMm,      setGapMm]      = useState(3);   // gap between labels (sheet mode)

  const money = n => Number(n || 0).toFixed(2);
  const visible  = products.filter(p => !search.trim() || p.name?.toLowerCase().includes(search.toLowerCase()) || p.barcode?.includes(search));
  const selected = products.filter(p => Number(copies[p._id]) > 0);
  const totalLabels = selected.reduce((s, p) => s + Number(copies[p._id]), 0);

  const handlePrint = () => {
    const labels = selected.flatMap(p => Array(Number(copies[p._id])).fill(p));
    if (!labels.length) { alert('Select at least one product.'); return; }

    const isRoll = mode === 'roll';
    const w = Number(widthMm) || 50;
    const h = Number(heightMm) || 30;
    const cols = isRoll ? 1 : (Number(columns) || 2);

    // px at 96 dpi: 1mm ≈ 3.78px
    const PX = v => Math.round(v * 3.78);
    const bcH   = Math.max(16, PX(h * 0.38));
    const bcW   = Math.max(1.2, isRoll ? 1.8 : 1.4);
    const nFnt  = Math.max(7,  PX(h * 0.13));
    const prFnt = Math.max(9,  PX(h * 0.20));
    const bcFnt = Math.max(6,  PX(h * 0.09));

    // per-design label HTML
    const makeLabelBody = (p, idx) => {
      const lbpOnly = !Number(p.price_usd) && Number(p.price_lbp);   // item priced in lira
      const price = (showLBP || lbpOnly)
        ? `${Math.round(lbpOnly ? p.price_lbp : (p.price_usd || 0) * rate).toLocaleString('en-US')} ل.ل`
        : `$${money(p.price_usd || 0)}`;
      const esc   = s => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;');
      const bc    = p.barcode || '';
      const svgEl = bc ? `<svg id="bc_${idx}" class="bcsvg"></svg>` : `<div class="nobc">NO BARCODE</div>`;
      const bcNum = bc ? `<div class="bcnum">${bc}</div>` : '';
      const shopEl = (showShop && shopName) ? `<div class="shopname">${esc(shopName)}</div>` : '';

      if (design === 'price') {
        return `${shopEl}<div class="price">${price}</div>${svgEl}${bcNum}`;
      }
      if (design === 'minimal') {
        return `${shopEl}${svgEl}${bcNum}<div class="price">${price}</div>`;
      }
      // standard
      return `${shopEl}<div class="pname">${esc(p.name)}</div>${svgEl}${bcNum}<div class="price">${price}</div>`;
    };

    const labelHtml = labels.map((p, idx) =>
      `<div class="label">${makeLabelBody(p, idx)}</div>`
    ).join('');

    // JsBarcode init for each label (unique index → unique id)
    const barcodeInits = labels.map((p, idx) => {
      if (!p.barcode) return '';
      return `try{JsBarcode("#bc_${idx}",${JSON.stringify(p.barcode)},{format:${JSON.stringify(bcFormat)},width:${bcW},height:${bcH},displayValue:false,margin:0});}catch(e){document.getElementById("bc_${idx}").outerHTML='<div style="font-size:7px;color:red">Invalid for ${bcFormat}</div>';}`;
    }).join('\n');

    const pageStyle = isRoll
      ? `@page{size:${w}mm ${h}mm;margin:0}body{margin:0}`
      : `@page{size:A4;margin:${Number(gapMm)||3}mm}body{margin:0}`;

    const gridStyle = isRoll
      ? `.grid{display:block}.label{width:${w}mm;min-height:${h}mm;page-break-after:always;break-after:page;box-sizing:border-box}`
      : `.grid{display:grid;grid-template-columns:repeat(${cols},1fr);gap:${Number(gapMm)||3}mm}.label{break-inside:avoid;page-break-inside:avoid;min-height:${h}mm}`;

    // design-specific styles
    const designStyles = {
      standard: `.pname{font-size:${nFnt}px;font-weight:700;color:#111;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;margin-bottom:2px}.price{font-size:${prFnt}px;font-weight:800;color:#1a1a1a;margin-top:2px}`,
      price:    `.price{font-size:${Math.round(prFnt*1.5)}px;font-weight:800;color:#2e9f86;margin-bottom:3px}.pname{display:none}`,
      minimal:  `.price{font-size:${prFnt}px;font-weight:700;color:#111;margin-top:2px}`,
    }[design] || '';

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Labels</title>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"><\/script>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;background:#fff;-webkit-print-color-adjust:exact}
  ${pageStyle}
  ${gridStyle}
  .label{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;overflow:hidden;padding:${Number(marginMm)||1.5}mm}
  .bcsvg{width:100%;display:block;max-height:${bcH}px}
  .bcnum{font-size:${bcFnt}px;color:#555;letter-spacing:0.8px;margin:1px 0;font-family:monospace}
  .nobc{font-size:${bcFnt}px;color:#bbb;margin:4px 0;font-style:italic}
  .shopname{font-size:${Math.max(5,PX(h*0.09))}px;font-weight:600;color:#2e9f86;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:100%;text-align:center}
  ${designStyles}
  @media print{*{-webkit-print-color-adjust:exact}}
</style></head><body>
<div class="grid">${labelHtml}</div>
<script>
window.onload = function() {
  ${barcodeInits}
  setTimeout(function(){ window.print(); setTimeout(function(){ window.close(); }, 600); }, 500);
};
<\/script></body></html>`;

    const popup = window.open('', '_blank', 'width=960,height=720');
    if (popup) { popup.document.write(html); popup.document.close(); }
  };

  const inpS = { border: '1.5px solid #e5e7eb', borderRadius: 7, padding: '6px 9px', fontSize: 12, background: '#fafafa', outline: 'none' };
  const previewH = Math.max(32, Math.min(80, Number(heightMm) * 1.4));
  const previewW = Math.max(60, Math.min(160, Number(widthMm) * 1.4));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 48px rgba(0,0,0,0.22)' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>🏷️ Print Barcode Labels</div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>✕</button>
        </div>

        {/* Config panel */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', background: '#f9fafb', display: 'flex', gap: 16, flexWrap: 'wrap' }}>

          {/* Left: size + barcode type */}
          <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Row 1: mode + size */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={mode} onChange={e => setMode(e.target.value)} style={inpS}>
                <option value="roll">🗞️ Roll</option>
                <option value="sheet">📄 A4 Sheet</option>
              </select>
              <input type="number" value={widthMm} min="20" max="200" onChange={e => setWidthMm(e.target.value)}
                style={{ ...inpS, width: 52 }} title="Width mm" />
              <span style={{ fontSize: 11, color: '#9ca3af' }}>×</span>
              <input type="number" value={heightMm} min="15" max="200" onChange={e => setHeightMm(e.target.value)}
                style={{ ...inpS, width: 52 }} title="Height mm" />
              <span style={{ fontSize: 11, color: '#9ca3af' }}>mm</span>
              {mode === 'sheet' && (
                <select value={columns} onChange={e => setColumns(Number(e.target.value))} style={{ ...inpS, width: 54 }} title="Columns">
                  {[2,3,4,5].map(n => <option key={n} value={n}>{n} col</option>)}
                </select>
              )}
            </div>
            {/* Quick presets */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#9ca3af', marginRight: 2 }}>QUICK:</span>
              {[['40×25',40,25,'roll'],['50×30',50,30,'roll'],['60×40',60,40,'roll'],['80×50',80,50,'roll'],['100×50',100,50,'roll'],['A4 3col',210,297,'sheet']].map(([lbl,pw,ph,pm]) => (
                <button key={lbl} onClick={() => { setWidthMm(pw); setHeightMm(ph); setMode(pm); if(pm==='sheet') setColumns(3); }}
                  style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 7px', fontSize: 10, cursor: 'pointer', color: '#374151' }}>
                  {lbl}
                </button>
              ))}
            </div>
            {/* Row 2: margin + gap */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>Margin:</span>
              <input type="number" value={marginMm} min="0" max="10" step="0.5" onChange={e => setMarginMm(e.target.value)}
                style={{ ...inpS, width: 52 }} title="Inner padding inside each label (mm)" />
              <span style={{ fontSize: 11, color: '#9ca3af' }}>mm</span>
              {mode === 'sheet' && <>
                <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>Gap:</span>
                <input type="number" value={gapMm} min="0" max="20" step="0.5" onChange={e => setGapMm(e.target.value)}
                  style={{ ...inpS, width: 52 }} title="Space between labels on sheet (mm)" />
                <span style={{ fontSize: 11, color: '#9ca3af' }}>mm</span>
              </>}
            </div>
            {/* Row 3: barcode format */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>Barcode type:</span>
              <select value={bcFormat} onChange={e => setBcFormat(e.target.value)} style={{ ...inpS, flex: 1, minWidth: 110 }}>
                {BARCODE_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label} — {f.hint}</option>)}
              </select>
            </div>
            {/* Row 3: toggles */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={showLBP} onChange={e => setShowLBP(e.target.checked)} />
                Show LBP price
              </label>
              {shopName && (
                <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={showShop} onChange={e => setShowShop(e.target.checked)} />
                  Show shop name <span style={{ color: '#2e9f86', fontWeight: 600 }}>({shopName})</span>
                </label>
              )}
            </div>
          </div>

          {/* Right: design picker + preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>Label design:</span>
            <div style={{ display: 'flex', gap: 6 }}>
              {LABEL_DESIGNS.map(d => (
                <button key={d.id} onClick={() => setDesign(d.id)}
                  style={{ border: `2px solid ${design===d.id?'#2e9f86':'#e5e7eb'}`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', background: design===d.id?'#f0fdfb':'#fff', textAlign: 'left' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: design===d.id?'#2e9f86':'#374151' }}>{d.name}</div>
                  <div style={{ fontSize: 9, color: '#9ca3af', marginTop: 1 }}>{d.desc}</div>
                </button>
              ))}
            </div>
            {/* Live preview box */}
            <div style={{ marginTop: 4, background: '#fff', border: '1.5px dashed #d1d5db', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
              <div style={{ width: previewW, height: previewH, border: '1px solid #374151', borderRadius: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 3, gap: 1, overflow: 'hidden', background: '#fff' }}>
                {showShop && shopName && <div style={{ fontSize: 6, fontWeight: 700, color: '#2e9f86', textTransform: 'uppercase', letterSpacing: 0.5, width: '100%', textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{shopName}</div>}
                {design !== 'minimal' && <div style={{ fontSize: 7, fontWeight: 700, color: '#111', width: '100%', textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>Product Name</div>}
                {/* fake barcode bars */}
                <div style={{ width: '85%', display: 'flex', gap: '1px', alignItems: 'flex-end', height: design==='price'?Math.round(previewH*0.22):Math.round(previewH*0.33) }}>
                  {[3,1,2,3,1,2,1,3,2,1,3,2,1,2,3,1,2,3,1,2,1,3].map((wt,i) => (
                    <div key={i} style={{ flex: wt, background: '#222', height: '100%' }} />
                  ))}
                </div>
                <div style={{ fontSize: 6, color: '#555', fontFamily: 'monospace' }}>123456789012</div>
                <div style={{ fontSize: design==='price'?10:8, fontWeight: 800, color: design==='price'?'#2e9f86':'#111' }}>$12.50</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '8px 20px', borderBottom: '1px solid #f0f0f0' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
            style={{ width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8, padding: '7px 11px', fontSize: 13, boxSizing: 'border-box' }} />
        </div>

        {/* Product list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px' }}>
          {visible.map(p => (
            <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', gap: 6, alignItems: 'center' }}>
                  {p.barcode
                    ? <span style={{ fontFamily: 'monospace' }}>{p.barcode}</span>
                    : <span style={{ color: '#f59e0b' }}>⚠ no barcode</span>}
                  <span>· {!Number(p.price_usd) && Number(p.price_lbp) ? `${Math.round(p.price_lbp).toLocaleString('en-US')} LBP` : `$${money(p.price_usd)}`}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <button onClick={() => setCopies(c => ({ ...c, [p._id]: Math.max(0, (Number(c[p._id])||0)-1) }))}
                  style={{ width: 26, height: 26, border: '1.5px solid #e5e7eb', borderRadius: 6, background: '#f9fafb', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>−</button>
                <input type="number" min={0} max={999} step={1}
                  value={copies[p._id] || ''}
                  placeholder="0"
                  onChange={e => setCopies(c => ({ ...c, [p._id]: Math.max(0, parseInt(e.target.value)||0) }))}
                  style={{ width: 50, border: '1.5px solid #e5e7eb', borderRadius: 6, padding: '4px 6px', fontSize: 13, textAlign: 'center' }} />
                <button onClick={() => setCopies(c => ({ ...c, [p._id]: (Number(c[p._id])||0)+1 }))}
                  style={{ width: 26, height: 26, border: '1.5px solid #e5e7eb', borderRadius: 6, background: '#f9fafb', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>+</button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#6b7280', flex: 1 }}>
            {selected.length} product(s) · <strong>{totalLabels}</strong> label(s) · {widthMm}×{heightMm}mm · margin {marginMm}mm{mode==='sheet'?` · gap ${gapMm}mm`:''} · {bcFormat}
          </span>
          <button onClick={handlePrint} disabled={!totalLabels}
            style={{ background: '#2e9f86', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: !totalLabels ? 0.5 : 1 }}>
            Print Labels
          </button>
        </div>
      </div>
    </div>
  );
}
